import { ApiResponse } from '@shared/types';
import {
  JournalEntryLineRecord,
  SaveJournalEntryLineRequest,
} from '../types/accounting';
import { supabase } from './supabase';

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  return Number(value || 0);
};

const mapJournalEntryLine = (
  value: Record<string, unknown>,
): JournalEntryLineRecord => ({
  id: String(value.id || ''),
  company_id: toNumber(value.company_id),
  journal_entry_id: String(value.journal_entry_id || ''),
  account_id: String(value.account_id || ''),
  outlet_id: (value.outlet_id as string | null) ?? null,
  debit: toNumber(value.debit),
  credit: toNumber(value.credit),
  description: (value.description as string | null) ?? null,
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
});

const ensureDraftJournalEntry = async (
  companyId: number,
  journalEntryId: string,
): Promise<ApiResponse<null>> => {
  const { data, error } = await supabase
    .from('journal_entries')
    .select('id, status')
    .eq('company_id', companyId)
    .eq('id', journalEntryId)
    .maybeSingle();

  if (error) {
    return { success: false, error: error.message, data: null };
  }

  if (!data) {
    return { success: false, error: 'Journal entry not found', data: null };
  }

  if (data.status !== 'draft') {
    return {
      success: false,
      error: 'Only draft journal entries can be modified',
      data: null,
    };
  }

  return { success: true, data: null };
};

export const fetchJournalEntryLines = async (
  companyId: number,
  journalEntryId: string,
): Promise<ApiResponse<JournalEntryLineRecord[]>> => {
  try {
    const { data, error } = await supabase
      .from('journal_entry_lines')
      .select('*')
      .eq('company_id', companyId)
      .eq('journal_entry_id', journalEntryId)
      .order('created_at', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item) => mapJournalEntryLine(item)),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch journal entry lines',
      data: [],
    };
  }
};

export const saveJournalEntryLine = async (
  input: SaveJournalEntryLineRequest,
): Promise<ApiResponse<JournalEntryLineRecord>> => {
  const draftGuard = await ensureDraftJournalEntry(
    input.companyId,
    input.journalEntryId,
  );

  if (!draftGuard.success) {
    return { success: false, error: draftGuard.error, data: undefined };
  }

  const payload = {
    company_id: input.companyId,
    journal_entry_id: input.journalEntryId,
    account_id: input.accountId,
    outlet_id: input.outletId ?? null,
    debit: input.debit ?? 0,
    credit: input.credit ?? 0,
    description: input.description?.trim() || null,
  };

  try {
    if (input.id) {
      const { data, error } = await supabase
        .from('journal_entry_lines')
        .update(payload)
        .eq('company_id', input.companyId)
        .eq('journal_entry_id', input.journalEntryId)
        .eq('id', input.id)
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      return { success: true, data: mapJournalEntryLine(data) };
    }

    const { data, error } = await supabase
      .from('journal_entry_lines')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapJournalEntryLine(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save journal entry line',
      data: undefined,
    };
  }
};

export const deleteJournalEntryLine = async (
  companyId: number,
  journalEntryId: string,
  journalEntryLineId: string,
): Promise<ApiResponse<null>> => {
  const draftGuard = await ensureDraftJournalEntry(companyId, journalEntryId);

  if (!draftGuard.success) {
    return { success: false, error: draftGuard.error, data: null };
  }

  try {
    const { error } = await supabase
      .from('journal_entry_lines')
      .delete()
      .eq('company_id', companyId)
      .eq('journal_entry_id', journalEntryId)
      .eq('id', journalEntryLineId);

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: null };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to delete journal entry line',
      data: null,
    };
  }
};
